import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { PLACE_POINTS, rankOf, type CardId, type Seat } from '@basgan/rules';
import type { PublicSeat, ViewMessage } from '@basgan/protocol';
import type { DisplayState } from '../game/display.js';
import { toggleExchange } from '../game/selection.js';
import { isPlayable, resolveTap } from '../game/tap.js';
import { Card, CardBack, CardChip, type Direction, type Entry } from './Card.js';
import { TimerRing, TIMER_TEXT_MIN_MS } from './TimerRing.js';
import { Mascot } from './Mascot.js';

/** Sunucu `deadlineTotal` göndermediyse (eski sürüm) varsayılan. */
const TURN_TOTAL_MS = 15_000;
/** El başı kart sayısı — kart göstergesindeki yuva adedi. */
const HAND_MAX = 13;
/** Maç yapısı. Kural motorunun varsayılan yapılandırmasıyla aynı. */
const DEALS_PER_SET = 4;
const SETS = 4;
const TOTAL_DEALS = DEALS_PER_SET * SETS;

/** Kendi koltuğum dahil dört kenar. `Direction` yalnızca kartın uçtuğu yön. */
type SeatPos = Direction | 'bottom';

interface Props {
  view: ViewMessage;
  display: DisplayState;
  seats: PublicSeat[];
  busy: boolean;
  /** Ekranda duran hazır mesajlar: koltuk → cümle. */
  chat: Partial<Record<Seat, string>>;
  /** Seçilebilir hazır mesajlar — sunucudan geliyor, gömülü değil. */
  phrases: string[];
  /** "Ver" gönderildi, sunucu onayı bekleniyor. */
  exchangeSent: boolean;
  /** Değişim sonucu perdesi ekranda mı? */
  exchangeResult: boolean;
  onPlay(cards: CardId[]): void;
  onPass(): void;
  onExchange(cards: CardId[]): void;
  onChat(text: string): void;
  onQuit(): void;
  /** Otomatik pas açık mı — kapalıyken oynayacak kart yokken de oyuncu bekleniyor. */
  autoPass: boolean;
  onToggleAutoPass(): void;
  /** Masanın yöneticisi ben miyim — maskotu yalnızca o salabiliyor. */
  isHost: boolean;
  /**
   * Maskot turu sayacı. Sunucudan geliyor: yönetici saldığında masadaki
   * HERKESTE artıyor, yani animasyonu bir kişi başlatıp herkes izliyor.
   */
  mascotRun: number;
  onMascot(): void;
}

export function Table({
  view,
  display,
  seats,
  busy,
  chat,
  phrases,
  exchangeSent,
  exchangeResult,
  onPlay,
  onPass,
  onExchange,
  onChat,
  onQuit,
  autoPass,
  onToggleAutoPass,
  isHost,
  mascotRun,
  onMascot,
}: Props) {
  /** Yalnızca DEĞİŞİM fazında kullanılır; oyunda seçim yok, doğrudan atılıyor. */
  const [selected, setSelected] = useState<CardId[]>([]);
  /** Lider konumunda "kaç tane?" sorusu — kart grubunun üstünde beliren sayılar. */
  const [chooser, setChooser] = useState<{ index: number; options: CardId[][] } | null>(null);
  /** Sıralama tablosu açık mı — HUD kutusuna basınca açılıyor. */
  const [boardOpen, setBoardOpen] = useState(false);
  /** Hazır mesaj listesi açık mı — kendi avatarıma dokununca açılıyor. */
  const [chatOpen, setChatOpen] = useState(false);
  /**
   * Maskot ekranda mı.
   *
   * Tetik SUNUCUDAN geliyor (`mascotRun` sayacı): yönetici düğmeye basınca
   * masadaki herkeste aynı anda koşuyor. Sayaç — bayrak değil — çünkü art
   * arda iki tur ayırt edilebilmeli; bayrak olsaydı ikincisi hiç görünmezdi.
   */
  const [mascotOn, setMascotOn] = useState(false);
  const lastMascotRun = useRef(mascotRun);
  useEffect(() => {
    if (mascotRun === lastMascotRun.current) return;
    lastMascotRun.current = mascotRun;
    setMascotOn(true);
  }, [mascotRun]);
  /**
   * Çıkış onayı açık mı.
   *
   * ✕ doğrudan çıkarıyordu ve düğme elin hemen üstünde, parmağın gezindiği
   * yerde duruyor — yanlışlıkla basılınca maç terk edilmiş sayılıyor, koltuk
   * bota geçiyordu. Geri dönüşü olmayan tek hareket bu, o yüzden soruyoruz.
   */
  const [quitOpen, setQuitOpen] = useState(false);

  /**
   * Oyun alanının ölçüsü. Rakip kartının uçuş başlangıcı buradan hesaplanıyor;
   * sabit piksel verilince masa büyüyünce kart oyuncunun yerinden değil
   * ortadan doğuyordu.
   */
  const playRef = useRef<HTMLDivElement>(null);
  const [play, setPlay] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = playRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setPlay({ w: el.clientWidth, h: el.clientHeight });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const myTurn = view.phase === 'play' && view.turn === view.you && !busy;
  /**
   * Elden kart seçilebiliyor mu — seçimini verdikten sonra kilitleniyor.
   * `exchangeSent` de sayılıyor: sunucunun onayını beklerken panel hâlâ seçim
   * ekranını gösteriyordu ve "Ver" tepkisiz görünüyordu.
   */
  const sent = exchangeSent || !!view.exchange?.submitted;
  const exchanging = view.phase === 'exchange' && !!view.exchange?.mustGive && !sent;
  /** Değişimde kaç kart verilecek. */
  const needed = view.exchange?.mustGive?.count ?? 0;
  const canPass = myTurn && view.legalMoves.some((m) => m.type === 'pass');

  // Durum değişince açık kalan "kaç tane?" sorusu anlamını yitirir.
  useEffect(() => setChooser(null), [view.seq]);

  /**
   * Değişim seçimi YALNIZCA el ya da faz değişince sıfırlanır.
   * Her `seq`'te sıfırlamak bir hataydı: karşı taraf kartlarını verdiğinde
   * gelen görünüm, oyuncunun yarım kalan seçimini sessizce siliyordu.
   */
  useEffect(() => setSelected([]), [view.dealIndex, view.phase]);

  const tap = (card: CardId, index: number) => {
    if (exchanging) {
      // Değişimde rütbe kısıtı yok — `toggleSelection` kullanılıyordu ve
      // farklı rütbeden ikinci kartı seçtirmiyordu.
      setSelected((current) => toggleExchange(current, card, needed));
      return;
    }
    const result = resolveTap(view, card);
    if (result.kind === 'play') {
      setChooser(null);
      onPlay(result.cards);
    } else if (result.kind === 'choose') {
      setChooser({ index, options: result.options });
    }
  };

  // Koltukları kendi koltuğum altta olacak şekilde döndür.
  const order: Seat[] = [0, 1, 2, 3].map((i) => ((view.you + i) % 4) as Seat);
  const [mySeat, rightSeat, topSeat, leftSeat] = order as [Seat, Seat, Seat, Seat];

  /**
   * Atılan kartın nereden uçacağı — yığının merkezine göre sapma.
   * Kendi kartım `layoutId` ile elimden uçtuğu için sapmaya ihtiyacı yok.
   * Ölçüm gelmeden önce (ilk kare) makul bir alt sınır kullanılıyor.
   */
  const entryOf = (seat: Seat): Entry | undefined => {
    if (seat === topSeat) return { x: 0, y: -Math.max(160, play.h / 2 - 50), rotate: 8 };
    if (seat === leftSeat) return { x: -Math.max(180, play.w / 2 - 40), y: 0, rotate: -14 };
    if (seat === rightSeat) return { x: Math.max(180, play.w / 2 - 40), y: 0, rotate: 14 };
    return undefined;
  };

  const handSize = display.hand.length;
  // Sıralamadaki yerim — HUD'daki #n rozeti.
  const rank =
    [...view.scores].sort((a, b) => b - a).indexOf(view.scores[view.you] ?? 0) + 1;

  // Halkanın ne kadar dolu çizileceği sunucudan geliyor: hamle, değişim ve bot
  // düşünme süreleri farklı, istemci tek bir sabiti varsayamaz.
  const timerTotal = view.deadlineTotal ?? TURN_TOTAL_MS;
  const seatProps = { view, display, seats, chat, total: timerTotal };
  const nameOf = (seat: Seat) => seats[seat]?.name ?? `Koltuk ${seat}`;

  return (
    <div className="table">
      {/* Masa: ekranın ortasında duran dikdörtgen çuha. HER ŞEY bunun içinde —
          rakipler kenarlarda, atılan kartlar merkezde, kendi elim alt kenarda.
          Dışarıda bir şey kalınca masa "yüzüyor" gibi duruyordu. */}
      <div className="board">
        {/* HUD aynı zamanda sıralama tablosunun düğmesi — durum göstergesi ve
            ona açılan kapı aynı yerde. */}
        <button
          type="button"
          className="hud"
          onClick={() => setBoardOpen(true)}
          title="Sıralamayı gör"
        >
          <span className="hud__row">
            <span className="hud__rank">#{rank}</span>
            <span className="hud__deal">
              <b>{view.dealIndex + 1}/{TOTAL_DEALS}</b>
              <em>EL</em>
            </span>
          </span>
          <span className="hud__set">SET {view.set}/{SETS}</span>
        </button>

        {/* Otomatik pas anahtarı — çıkışın solunda, aynı köşede.

            İkon "sırayı atla": oynayacak kartın yokken sıranın kendiliğinden
            geçmesi. Emoji yerine çizim, çünkü emoji her cihazda başka türlü
            görünüyor ve açık/kapalı halini renkle anlatmak emojide mümkün
            değil (kendi renklerini taşıyor). */}
        <button
          className={autoPass ? 'board__auto board__auto--on' : 'board__auto'}
          onClick={onToggleAutoPass}
          title={
            autoPass
              ? 'Otomatik pas açık — oynayacak kartın yoksa sıra kendiliğinden geçer'
              : 'Otomatik pas kapalı — pas kararı sende'
          }
          aria-label="Otomatik pas"
          aria-pressed={autoPass}
          type="button"
        >
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
            <path d="M5 5.5 14 12l-9 6.5v-13Z" fill="currentColor" />
            <rect x="16" y="5.5" width="2.6" height="13" rx="1.3" fill="currentColor" />
          </svg>
        </button>

        {/* Maskot düğmesi YALNIZCA yöneticide: masanın tamamını ilgilendiren
            bir gösteri, herkesin tetikleyebilmesi masayı çöpe çevirirdi. */}
        {isHost && (
        <button
          className="board__mascot"
          onClick={onMascot}
          title="Maskotu salıver"
          aria-label="Maskotu salıver"
          type="button"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
            <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M12 3v18M3 12h18" />
              <path d="M5.6 5.6C8 7.9 9.2 9.9 9.2 12s-1.2 4.1-3.6 6.4" />
              <path d="M18.4 5.6C16 7.9 14.8 9.9 14.8 12s1.2 4.1 3.6 6.4" />
            </g>
          </svg>
        </button>
        )}

        <button
          className="board__quit"
          onClick={() => setQuitOpen(true)}
          title="Oyundan çık"
          type="button"
        >
          ✕
        </button>

        {/* Oyun alanı: koltukların konumu ve yığının merkezi buna göre —
            böylece yan plakalar yığınla aynı hizada kalıyor. */}
        <div className="board__play" ref={playRef}>
          <PlayerSeat seat={topSeat} position="top" {...seatProps} />
          <PlayerSeat seat={leftSeat} position="left" {...seatProps} />
          <PlayerSeat seat={rightSeat} position="right" {...seatProps} />

          <div className="pile">
            <AnimatePresence>
              {display.pile.map((entry, index) => (
                <motion.div
                  key={`${entry.seat}-${index}`}
                  className="pile__group"
                  // Kayıklık `style` ile sabit veriliyor, animasyonla değil:
                  // grup hareket ederken içindeki kartın uçuşu bozulmasın.
                  style={{
                    zIndex: index,
                    x: ((index * 13) % 17) - 8,
                    y: index * -4,
                    rotate: ((index * 37) % 15) - 7,
                  }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.22 } }}
                >
                  {entry.cards.map((card) => (
                    <Card key={card} card={card} from={entryOf(entry.seat)} />
                  ))}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

        </div>

        {/* Masanın önünde, diyalogların arkasında. `key` ile yeniden kuruluyor:
            çalan animasyonun ortasında tekrar basılsa bile baştan başlıyor. */}
        {mascotOn && <Mascot key={mascotRun} onDone={() => setMascotOn(false)} />}

        {/* Diyaloglar masanın DOĞRUDAN çocuğu, oyun alanının değil: alt bant
            kendi yığın bağlamını kurduğu için oyun alanının içindeyken
            plakanın ve elin ARKASINDA kalıyorlardı. Yuva tıklamayı geçiriyor —
            değişimde kartlar yine elden seçilecek. */}
        <div className="dialog-slot">
          <AnimatePresence mode="wait">
            {/* Sıra bilerek katı: el sonu özeti oynarken görünüm çoktan
                'exchange' fazına geçmiş oluyor, ikisi üst üste binerdi. */}
            {display.dealSummary ? (
              <DealSummary
                key="deal"
                summary={display.dealSummary}
                dealIndex={view.dealIndex}
                nameOf={nameOf}
                you={view.you}
              />
            ) : view.phase === 'exchange' && view.exchange ? (
              <ExchangePanel
                key="exchange"
                exchange={view.exchange}
                sent={sent}
                deadline={view.deadline}
                total={timerTotal}
                nameOf={nameOf}
                selected={selected}
                onSubmit={() => onExchange(selected)}
              />
            ) : exchangeResult && view.lastExchange ? (
              <ExchangeResult key="result" summary={view.lastExchange} nameOf={nameOf} />
            ) : null}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {boardOpen && (
            <Scoreboard
              view={view}
              seats={seats}
              onClose={() => setBoardOpen(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {quitOpen && (
            <QuitConfirm onCancel={() => setQuitOpen(false)} onConfirm={onQuit} />
          )}
        </AnimatePresence>

        <div className="board__bottom">
          {/* Kendi plakam ve durum satırı yan yana: alt kenarda dikey yer
              kazanıyor, el kartları için daha çok alan kalıyor. */}
          <div className="board__self">
            <PlayerSeat
              seat={mySeat}
              position="bottom"
              showCards={false}
              onAvatar={() => setChatOpen((open) => !open)}
              {...seatProps}
            />

            {/* Liste avatarın hemen üstünde açılıyor — perde yok, oyun
                kilitlenmiyor. */}
            <AnimatePresence>
              {chatOpen && (
                <ChatPicker
                  phrases={phrases}
                  onPick={(text) => {
                    setChatOpen(false);
                    onChat(text);
                  }}
                  onClose={() => setChatOpen(false)}
                />
              )}
            </AnimatePresence>

            <div className="hand-area__status">
              {/* Değişim arayüzü masanın ortasındaki panelde; burada yalnızca
                  oyun içi eylem var. Sırayı bildiren yazı yok — hangi plakada
                  geri sayım dönüyorsa sıra onda. */}
              {myTurn && canPass && (
                <button type="button" className="btn btn--primary btn--pill" onClick={onPass}>
                  Pas
                </button>
              )}
            </div>
          </div>

          <div className="hand-wrap">
            {/* Lider konumunda "aynı rütbeden kaç tane?" sorusu tam o kart
                grubunun üstünde beliriyor — ayrı bir düğme çubuğu yok. */}
            <AnimatePresence>
              {chooser && (
                <motion.div
                  className="count-picker"
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  style={{
                    left: `calc(50% + ${(chooser.index - (handSize - 1) / 2).toFixed(3)} * var(--hand-step))`,
                  }}
                >
                  {chooser.options.map((cards) => (
                    <button
                      key={cards.length}
                      type="button"
                      className="count-picker__btn"
                      onClick={() => {
                        setChooser(null);
                        onPlay(cards);
                      }}
                    >
                      {cards.length}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div className="hand" layout>
              {display.hand.map((card, index) => (
                <Card
                  key={card}
                  card={card}
                  // El rütbeye göre sıralı; rütbe değişen her kart yeni grubu
                  // açıyor ve soluna ayırıcı boşluk giriyor. İlk kartta boşluk
                  // yok, yoksa el ortadan kayardı.
                  groupStart={
                    index > 0 && rankOf(card) !== rankOf(display.hand[index - 1]!)
                  }
                  selected={
                    exchanging
                      ? selected.includes(card)
                      : (chooser?.options[0]?.includes(card) ?? false)
                  }
                  // Değişim fazında vurgulama yok; oyunda atılabilir kartlar kalkar.
                  playable={exchanging || !myTurn ? undefined : isPlayable(view, card)}
                  onClick={myTurn || exchanging ? () => tap(card, index) : undefined}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sıralama tablosu — HUD kutusuna basınca açılıyor.
 *
 * Maç boyunca biriken puanlar tek yerde. Kaçıncı sırada olduğunu HUD zaten
 * söylüyor; buradaki tablo aradaki farkı ve kimin nerede olduğunu gösteriyor.
 */
/**
 * Çıkış onayı.
 *
 * Sonucu söylüyor, "emin misin?" diye sormakla yetinmiyor: oyuncunun bilmesi
 * gereken şey çıkarsa ne olacağı — koltuk bota devrediliyor ve maç sensiz
 * sürüyor. Varsayılan eylem VAZGEÇ; yanlışlıkla açılan diyalogda Esc, dışarı
 * dokunma ve ilk düğme hep aynı güvenli yere çıkıyor.
 */
function QuitConfirm({ onCancel, onConfirm }: { onCancel(): void; onConfirm(): void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.section
        className="dialog"
        initial={{ y: 16, scale: 0.96 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.97 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h3>OYUNDAN ÇIK</h3>
          <span className="dialog__sub">Koltuğun bota devredilir, maç sensiz sürer.</span>
        </header>
        <div className="quit__actions">
          <button className="btn btn--pill" onClick={onCancel} type="button" autoFocus>
            Vazgeç
          </button>
          <button className="btn btn--pill btn--danger" onClick={onConfirm} type="button">
            Çık
          </button>
        </div>
      </motion.section>
    </motion.div>
  );
}

function Scoreboard({
  view,
  seats,
  onClose,
}: {
  view: ViewMessage;
  seats: PublicSeat[];
  onClose(): void;
}) {
  // Esc ile kapanmalı: modal açıkken oyuncunun tek çıkışı fare olmasın.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = [...seats].sort(
    (a, b) => (view.scores[b.seat] ?? 0) - (view.scores[a.seat] ?? 0),
  );
  /** Bir oyuncunun o eldeki puanı; el henüz oynanmadıysa null. */
  const pointsIn = (deal: number, seat: Seat): number | null => {
    const order = view.finishOrders[deal];
    if (!order) return null;
    return PLACE_POINTS[order.indexOf(seat)] ?? 0;
  };

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.section
        className="dialog dialog--wide"
        initial={{ y: 16, scale: 0.96 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.97 }}
        // Tabloya tıklamak kapatmasın; kapatma yalnızca dışarıdan.
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h3>PUAN ÇİZELGESİ</h3>
          <span className="dialog__sub">
            El {view.dealIndex + 1}/{TOTAL_DEALS} · Set {view.set}/{SETS}
          </span>
        </header>

        {/* Yatay kaydırma: 16 el hiçbir ekrana sığmıyor. İsim ve toplam
            sütunları yapışık kalıyor — kaydırırken kimin satırına baktığını
            ve nerede durduğunu kaybetmiyorsun. */}
        <div className="sheet">
          <table className="board-table board-table--sheet">
            <thead>
              <tr>
                <th className="sheet__name" rowSpan={2}>
                  Oyuncu
                </th>
                {Array.from({ length: SETS }, (_, s) => (
                  <th key={s} className="sheet__set" colSpan={DEALS_PER_SET}>
                    SET {s + 1}
                  </th>
                ))}
                <th className="sheet__total" rowSpan={2}>
                  Puan
                </th>
              </tr>
              <tr>
                {Array.from({ length: TOTAL_DEALS }, (_, d) => (
                  <th
                    key={d}
                    className={`sheet__deal ${d === view.dealIndex ? 'is-now' : ''} ${
                      d % DEALS_PER_SET === 0 ? 'is-set-start' : ''
                    }`}
                  >
                    {d + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((info, index) => (
                <tr key={info.seat} className={info.seat === view.you ? 'is-me' : undefined}>
                  <td className="sheet__name">
                    <span className={`place place--${index + 1}`}>{index + 1}</span>
                    <span className="board-table__name">
                      {info.name}
                      {info.bot && <span className="tag tag--bot">bot</span>}
                      {/* "yok" bağlantısı kopan İNSAN için. Bot koltuğu zaten
                          hiç bağlanmıyor; ikisi birlikte "Bot Cemil BOT YOK"
                          gibi okunuyordu. */}
                      {!info.bot && !info.connected && <span className="tag tag--off">yok</span>}
                    </span>
                  </td>
                  {Array.from({ length: TOTAL_DEALS }, (_, d) => {
                    const points = pointsIn(d, info.seat);
                    return (
                      <td
                        key={d}
                        className={`sheet__cell ${points ? `sheet__cell--p${points}` : ''} ${
                          d % DEALS_PER_SET === 0 ? 'is-set-start' : ''
                        }`}
                      >
                        {points ?? '·'}
                      </td>
                    );
                  })}
                  <td className="sheet__total">{view.scores[info.seat] ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="btn btn--primary btn--pill" onClick={onClose}>
          Kapat
        </button>
      </motion.section>
    </motion.div>
  );
}

/**
 * El sonu diyaloğu. Bu elde kimin kaç puan aldığı ve yeni toplamlar.
 * Sıralama tablosuyla aynı satır yapısını kullanıyor — iki ekran arasında
 * gözün yeniden yer bulması gerekmiyor.
 */
function DealSummary({
  summary,
  dealIndex,
  nameOf,
  you,
}: {
  summary: NonNullable<DisplayState['dealSummary']>;
  dealIndex: number;
  nameOf: (seat: Seat) => string;
  you: Seat;
}) {
  return (
    <motion.section
      className="dialog dialog--deal"
      initial={{ opacity: 0, y: 18, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
    >
      <header className="dialog__head">
        <h3>EL BİTTİ</h3>
        <span className="dialog__sub">El {dealIndex}/{TOTAL_DEALS}</span>
      </header>

      <table className="board-table">
        <tbody>
          {summary.order.map((seat, index) => (
            <tr key={seat} className={seat === you ? 'is-me' : undefined}>
              <td className={`place place--${index + 1}`}>{index + 1}</td>
              <td className="board-table__name">{nameOf(seat)}</td>
              <td className="board-table__gain">+{4 - index}</td>
              <td className="board-table__score">{summary.scores[seat]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.section>
  );
}

/**
 * El sonu kart değişimi paneli.
 *
 * Masanın ortasında duruyor, alt köşede bir satır olarak değil: bu, elin
 * gidişatını belirleyen an ve oyuncunun kaçırmaması gerekiyor. Kartlar yine
 * ELDEN seçiliyor — panel yalnızca ne olduğunu anlatıyor ve onaylıyor.
 */
function ExchangePanel({
  exchange,
  sent,
  deadline,
  total,
  nameOf,
  selected,
  onSubmit,
}: {
  exchange: NonNullable<ViewMessage['exchange']>;
  /** Sunucu onayı beklense de seçim gönderildi sayılır. */
  sent: boolean;
  deadline: number | null;
  total: number;
  nameOf: (seat: Seat) => string;
  selected: CardId[];
  onSubmit(): void;
}) {
  const { received, lost, mustGive } = exchange;

  return (
    <motion.div
      className="xchg"
      initial={{ opacity: 0, y: 14, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
    >
      <header className="xchg__head">
        <h3>KART DEĞİŞİMİ</h3>
        {deadline !== null && <TimerRing deadline={deadline} total={total} />}
      </header>

      {received.length > 0 && (
        <p className="xchg__line xchg__line--gain">
          <em>Sana geçen</em>
          <span className="xchg__cards">
            {received.map((c) => (
              <CardChip key={c} card={c} />
            ))}
          </span>
        </p>
      )}

      {lost.length > 0 && (
        <p className="xchg__line xchg__line--loss">
          <em>Senden alınan</em>
          <span className="xchg__cards">
            {lost.map((c) => (
              <CardChip key={c} card={c} />
            ))}
          </span>
        </p>
      )}

      {mustGive ? (
        sent ? (
          <p className="xchg__wait">Kartların gitti — karşı taraf bekleniyor…</p>
        ) : (
          <div className="xchg__pick">
            {/* Açıklama cümlesi kaldırıldı: paneli uzatıp elin önünü
                kapatıyordu, üstelik boş yuvalar zaten "kart seç" diyor. */}
            <p>
              <b>{nameOf(mustGive.to)}</b> oyuncusuna <b>{mustGive.count} kart</b>
            </p>
            <div className="xchg__chosen">
              {Array.from({ length: mustGive.count }, (_, i) => {
                const card = selected[i];
                return card !== undefined ? (
                  <CardChip key={card} card={card} />
                ) : (
                  <span key={`slot-${i}`} className="chip-card chip-card--empty" />
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn--primary btn--pill"
              disabled={selected.length !== mustGive.count}
              onClick={onSubmit}
            >
              Ver
            </button>
          </div>
        )
      ) : (
        <p className="xchg__wait">Karşı tarafın seçimi bekleniyor…</p>
      )}
    </motion.div>
  );
}

/**
 * Değişim bittiğinde iki tarafın da gördüğü özet.
 * Kaybeden, kazananın hangi kartları seçtiğini ancak burada öğreniyor.
 */
function ExchangeResult({
  summary,
  nameOf,
}: {
  summary: NonNullable<ViewMessage['lastExchange']>;
  nameOf: (seat: Seat) => string;
}) {
  return (
    <motion.div
      className="xchg xchg--result"
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.04 }}
    >
      <header className="xchg__head">
        <h3>{nameOf(summary.partner).toUpperCase()} İLE DEĞİŞİM</h3>
      </header>

      {/* İki kutu yan yana ve aralarında takas oku: hangi kartın hangi yöne
          gittiği tek bakışta okunuyor. Alt alta iki satırken yön bilgisini
          yalnızca "Aldın/Verdin" yazısı taşıyordu. */}
      <div className="swap">
        <div className="swap__side swap__side--in">
          <span className="swap__label">Aldın</span>
          <span className="swap__cards">
            {summary.received.map((c) => (
              <CardChip key={c} card={c} big />
            ))}
          </span>
        </div>

        <span className="swap__arrow" aria-hidden>
          ⇄
        </span>

        <div className="swap__side swap__side--out">
          <span className="swap__label">Verdin</span>
          <span className="swap__cards">
            {summary.given.map((c) => (
              <CardChip key={c} card={c} big />
            ))}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function PlayerSeat({
  seat,
  position,
  view,
  display,
  seats,
  chat,
  total,
  showCards = true,
  onAvatar,
}: {
  seat: Seat;
  position: SeatPos;
  view: ViewMessage;
  display: DisplayState;
  seats: PublicSeat[];
  chat: Partial<Record<Seat, string>>;
  /** Geri sayımın toplam süresi — halkanın doluluğu buna göre. */
  total: number;
  showCards?: boolean;
  /** Dolu ise avatar bir düğme olur (hazır mesaj listesini açar). */
  onAvatar?: (() => void) | undefined;
}) {
  const info = seats[seat];
  const badge = display.badges[seat];
  const active = view.turn === seat && view.phase === 'play';
  const name = info?.name ?? `Koltuk ${seat}`;
  const count = display.counts[seat] ?? 0;
  /** Halka dönüyor mu — rakip botlar dahil, sırası gelen herkeste. */
  const ringing = active && view.deadline !== null && total > 0;
  /** Rakam avatarın içinde mi — kısa sayımlarda baş harf kalıyor. */
  const counting = ringing && total >= TIMER_TEXT_MIN_MS;
  const chatText = chat[seat];

  return (
    <div className={`seat seat--${position} ${active ? 'seat--active' : ''}`}>
      {/* Plaka: skor jetonu dışta, avatar ortada, ad içeri bakıyor. */}
      <div className="seat__plate">
        <span className="seat__score">{view.scores[seat]}</span>

        {/* Sırası gelende avatarın baş harfi yerine kalan saniye görünüyor:
            iki bilgi aynı daireyi paylaşmasın. Süre yoksa (deadline null) baş
            harf kalır — yoksa daire bomboş görünüyordu. */}
        {/* Avatar ve mesaj balonu aynı kutuda: balon plakaya değil AVATARA
            çapalı. Plakanın genişliği ada ve skora göre değiştiği için plakaya
            çapalanınca balon her koltukta farklı yerden çıkıyordu. */}
        <span className="seat__avatar-slot">
          {/* Kendi avatarım bir düğme: dokununca hazır mesaj listesi açılıyor.
              Rakiplerinki düz bir kutu — onlara dokunmanın bir karşılığı yok. */}
          {onAvatar ? (
            <button
              type="button"
              className={`seat__avatar seat__avatar--tap ${counting ? 'seat__avatar--counting' : ''}`}
              onClick={onAvatar}
              title="Hazır mesaj gönder"
            >
              {!counting && name.slice(0, 1).toUpperCase()}
              {ringing && <TimerRing deadline={view.deadline} total={total} />}
              <span className="seat__avatar-hint" aria-hidden>
                💬
              </span>
            </button>
          ) : (
            <span className={`seat__avatar ${counting ? 'seat__avatar--counting' : ''}`}>
              {!counting && name.slice(0, 1).toUpperCase()}
              {ringing && <TimerRing deadline={view.deadline} total={total} />}
            </span>
          )}

          {/* Konumlama AYRI bir kutuda, animasyon içeride: Motion `transform`'u
              kendi yazıyor ve CSS'teki ortalamayı (`translateY(-50%)`) eziyor.
              Balon 14px aşağı kayıyordu. */}
          <span className="bubble-slot">
            <AnimatePresence>
              {chatText !== undefined && (
                <motion.div
                  key={`${chatText}-${seat}`}
                  className="bubble"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                >
                  {chatText}
                </motion.div>
              )}
            </AnimatePresence>
          </span>
        </span>

        <span className="seat__info">
          {/* Ad kendi elemanında: kırpma `text-overflow` ile üç noktaya
              dönüşsün. Doğrudan flex kutusunda olunca uzun adlar ortasından
              kesiliyordu ("ZEYNEP B"). */}
          <span className="seat__name">
            <span className="seat__name-text">{name}</span>
            {info?.bot && <span className="tag tag--bot">bot</span>}
            {info && !info.bot && !info.connected && <span className="tag tag--off">yok</span>}
          </span>
          {/* Yuva sayısı SABİT: oynanan kartlar sönüyor, silinmiyor. Yalnızca
              kalanları çizince plaka daralıyor ve ortalanmış plakalarda avatar
              yerinden oynuyordu. */}
          <span className="seat__pips" aria-label={`${count} kart`}>
            {Array.from({ length: HAND_MAX }, (_, i) => (
              <i key={i} className={i < count ? 'on' : ''} />
            ))}
            <b>{count}</b>
          </span>
        </span>

        {/* Rozet plakanın üstünde asılı duruyor: içine konunca adı ve kart
            göstergesini kapatıyordu. */}
        <AnimatePresence>
          {badge && (
            <motion.div
              className={`badge badge--${badge.kind}`}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {badge.kind === 'pass' ? 'PAS' : `${badge.place}.`}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showCards && (
        <div className="seat__cards">
          <AnimatePresence>
            {Array.from({ length: count }, (_, i) => (
              <CardBack key={i} index={i} side={position === 'left' || position === 'right'} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/**
 * Hazır mesaj listesi — avatarın hemen üstünde açılan kaydırmalı balon.
 *
 * Diyalog denendi ve bırakıldı: oyunu kilitliyordu. Mesaj göndermek sıra
 * beklemeyi gerektirmez, masanın durması için bir sebep yok. Liste dikey ve
 * kaydırmalı; sığdığı kadarı görünüyor, gerisi kaydırılıyor.
 *
 * Serbest metin yok: yatay tutulan telefonda klavye ekranın yarısını yiyor,
 * üstelik moderasyon derdi doğuruyor.
 */
function ChatPicker({
  phrases,
  onPick,
  onClose,
}: {
  phrases: string[];
  onPick(text: string): void;
  onClose(): void;
}) {
  const box = useRef<HTMLDivElement>(null);

  // Perde olmadığı için kapanma dışarı dokunuşla: liste kartların üstünde
  // asılı kalmamalı.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (box.current?.contains(target)) return;
      // Avatar düğmesi listeyi zaten açıp kapatıyor; burada karışmasın.
      if ((target as Element).closest?.('.seat__avatar--tap')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  }, [onClose]);

  return (
    <motion.div
      ref={box}
      className="chat-pop"
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 460, damping: 32 }}
    >
      {phrases.map((text) => (
        <button
          key={text}
          type="button"
          className="chat-pop__item"
          onClick={() => onPick(text)}
        >
          {text}
        </button>
      ))}
    </motion.div>
  );
}
